// Lazily-loaded Supabase client with anonymous auth (every device gets a user id from day 0,
// which the per-user scan quota and the retention cohorts depend on).

import type { SupabaseClient } from '@supabase/supabase-js';
import { captchaToken } from './captcha';
import { config, hasBackend } from './config';

let clientPromise: Promise<SupabaseClient> | null = null;

export function getSupabase(): Promise<SupabaseClient> {
  if (!hasBackend) return Promise.reject(new Error('Backend not configured'));
  clientPromise ??= import('@supabase/supabase-js').then(({ createClient }) =>
    createClient(config.supabaseUrl, config.supabaseAnonKey, {
      auth: { persistSession: true, autoRefreshToken: true, storageKey: 'snapcook_auth' },
    }),
  );
  return clientPromise;
}

/** Returns a valid access token, signing in anonymously on first use. */
export async function getAccessToken(): Promise<string> {
  const sb = await getSupabase();
  const { data } = await sb.auth.getSession();
  if (data.session) return data.session.access_token;
  const { data: signed, error } = await sb.auth.signInAnonymously({ options: { captchaToken: await captchaToken() } });
  if (error || !signed.session) throw new Error(error?.message ?? 'Sign-in failed');
  return signed.session.access_token;
}

/** GDPR erasure: deletes this device's server-side rows, then signs out. */
export async function deleteServerData(): Promise<void> {
  if (!hasBackend) return;
  const sb = await getSupabase();
  const { data } = await sb.auth.getSession();
  if (!data.session) return;
  const { error } = await sb.rpc('snapcook_delete_my_data');
  if (error) throw new Error(error.message);
  await sb.auth.signOut();
}
